import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  type BadgeProps,
  type DataTableColumn,
} from "@pericles/framework-frontend";

import { fetchProjectRequirementsCoverage } from "../api/projectRequirementsCoverageClient";
import type {
  ProjectRequirementsCoverageReport,
  RequirementTemplateCoverageEntry,
} from "../types/requirementsCoverage";

import { InlineAlert } from "./InlineAlert";
import { CoverageMatrixPanel } from "./CoverageMatrixPanel";

interface ProjectCoverageTabProps {
  projectId: string;
}

const STATUS_BADGES: Record<
  RequirementTemplateCoverageEntry["coverageStatus"],
  { label: string; variant: BadgeProps["variant"] }
> = {
  covered: { label: "Covered", variant: "success" },
  not_covered: { label: "Not covered", variant: "danger" },
};

function renderStatusBadge(status: RequirementTemplateCoverageEntry["coverageStatus"]): JSX.Element {
  const config = STATUS_BADGES[status] ?? { label: status, variant: "default" };
  return <Badge label={config.label} variant={config.variant} />;
}

function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function ProjectCoverageTab({ projectId }: ProjectCoverageTabProps): JSX.Element {
  const [report, setReport] = useState<ProjectRequirementsCoverageReport | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<RequirementTemplateCoverageEntry | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadCoverage = async () => {
      if (!projectId) return;

      setIsLoading(true);
      setError(null);

      try {
        const result = await fetchProjectRequirementsCoverage(projectId);
        if (!cancelled) {
          setReport(result);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Failed to load coverage";
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadCoverage();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

      const stats = useMemo(() => {
        const total = report?.entries.length ?? 0;
        const covered = report?.entries.filter((entry) => entry.coverageStatus === "covered").length ?? 0;
        const coveragePercent = total ? Math.round((covered / total) * 100) : 0;
        return { total, covered, coveragePercent };
      }, [report]);

  useEffect(() => {
    if (report?.entries?.length) {
      setSelectedTemplate((current) => current ?? report.entries[0]);
    }
  }, [report]);

      const columns = useMemo<DataTableColumn<RequirementTemplateCoverageEntry>[]>(
        () => [
          {
            id: "template",
            header: "Template",
            accessor: (row) => row.templateCode ?? row.templateId,
          },
      { id: "title", header: "Title", accessor: (row) => row.title },
      {
        id: "status",
        header: "Coverage",
        accessor: (row) => renderStatusBadge(row.coverageStatus),
        width: 140,
        align: "center",
      },
      {
        id: "linked",
        header: "Linked requirements",
        accessor: (row) => row.linkedRequirementIds.length,
        width: 180,
        align: "center",
      },
        ],
        [],
      );

  const handleSelectTemplate = (entry: RequirementTemplateCoverageEntry) => {
    setSelectedTemplate(entry);
  };

  const linkedRequirements = useMemo(
    () =>
      selectedTemplate
        ? selectedTemplate.linkedRequirements ??
          selectedTemplate.linkedRequirementIds.map((id) => ({ id, title: id }))
        : [],
    [selectedTemplate],
  );
  const hasLinkedRequirements = linkedRequirements.length > 0;
  const firstLinkedRequirementId = linkedRequirements[0]?.id;
  const creationBlocked =
    (selectedTemplate?.coverageStatus === "covered" || hasLinkedRequirements) && Boolean(selectedTemplate);
  const actionLabel = creationBlocked
    ? firstLinkedRequirementId
      ? "Open existing requirement"
      : "Requirement already covered"
    : "Create requirement for this template";

  const goToRequirementDetail = (requirementId: string) => {
    if (!projectId) return;

    const params = new URLSearchParams({
      tab: "requirements",
      requirementId,
    });

    navigate(`/projects/${projectId}?${params.toString()}`);
  };

  const handleRequirementAction = () => {
    if (!projectId || !selectedTemplate) return;

    if (creationBlocked) {
      if (firstLinkedRequirementId) {
        goToRequirementDetail(firstLinkedRequirementId);
      }
      return;
    }

    const params = new URLSearchParams({
      tab: "requirements",
      templateTitle: selectedTemplate.title,
    });
    if (selectedTemplate.templateCode) {
      params.set("templateCode", selectedTemplate.templateCode);
    }
    navigate(`/projects/${projectId}?${params.toString()}`);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Coverage" />

      {error ? <InlineAlert tone="danger" title={error} /> : null}

      <Card>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Overall coverage</div>
              <div className="text-sm text-slate-700">
                {stats.covered} of {stats.total || 0} templates covered
              </div>
            </div>
            <div className="text-3xl font-semibold text-slate-900">{stats.coveragePercent}%</div>
          </div>
          {report?.generatedAt ? (
            <div className="text-xs text-slate-600">Generated {formatDate(report.generatedAt)}</div>
          ) : null}
        </div>
      </Card>

      <Card title="Coverage by template">
        <DataTable<RequirementTemplateCoverageEntry>
          columns={columns}
          data={report?.entries ?? []}
          isLoading={isLoading}
          onRowClick={handleSelectTemplate}
          rowClassName={(row) =>
            selectedTemplate && row.templateId === selectedTemplate.templateId
              ? "data-table__row--active"
              : undefined
          }
          emptyState={
            <EmptyState
              title="No coverage data"
              description="No coverage entries available for this project yet."
            />
          }
        />
      </Card>

      <CoverageMatrixPanel projectId={projectId} />

      {selectedTemplate ? (
        <Card title="Template details">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700">{selectedTemplate.templateCode ?? selectedTemplate.templateId}</div>
                <div className="text-lg font-semibold text-slate-900">{selectedTemplate.title}</div>
              </div>
              {renderStatusBadge(selectedTemplate.coverageStatus)}
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-slate-800">Linked requirements</div>
              {linkedRequirements.length === 0 ? (
                <div className="rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  No requirements linked to this template yet.
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {linkedRequirements.map((requirement) => (
                    <li key={requirement.id}>
                      <button
                        type="button"
                        onClick={() => goToRequirementDetail(requirement.id)}
                        className="text-left text-sm font-semibold text-blue-700 hover:underline"
                      >
                        {requirement.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <button
                type="button"
                className="button button-primary"
                onClick={handleRequirementAction}
                disabled={creationBlocked && !firstLinkedRequirementId}
              >
                {actionLabel}
              </button>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
